// ===== 海运单枚举类型 =====

/** 船公司签收单状态 */
export const ShipReceiptStatusEnum = {
  NoNeed: 0, // 不需要上传
  NotUpload: 1, // 未上传
  UploadedNotBack: 2, // 已上传未拿回
  UploadedBack: 3, // 已上传已拿回
} as const;
export type ShipReceiptStatusEnum = typeof ShipReceiptStatusEnum[keyof typeof ShipReceiptStatusEnum];

/** 合作方签收单状态 */
export const PartnerReceiptStatusEnum = {
  NoNeed: 0, // 不需要处理
  NotTaken: 1, // 未拿回
  TakenNotPosted: 2, // 已拿回未邮寄
  NoNeedPost: 3, // 不需要寄出
  Posted: 4, // 已寄出
} as const;
export type PartnerReceiptStatusEnum = typeof PartnerReceiptStatusEnum[keyof typeof PartnerReceiptStatusEnum];

/** 箱况（跳号设计） */
export const BoxConditionEnum = {
  NoNeed: 0, // 不需要报备（进口默认）
  NotExec: 10, // 未报备（出口默认）
  Exec: 20, // 已报备
} as const;
export type BoxConditionEnum = typeof BoxConditionEnum[keyof typeof BoxConditionEnum];

/** 放货状态（默认=未放货） */
export const FhStatusEnum = {
  Unreleased: 0, // 未放货
  Released: 1, // 已放货
} as const;
export type FhStatusEnum = typeof FhStatusEnum[keyof typeof FhStatusEnum];

/** 加急状态 */
export const RushStatusEnum = {
  No: 0, // 不加急
  Yes: 1, // 加急
} as const;
export type RushStatusEnum = typeof RushStatusEnum[keyof typeof RushStatusEnum];

/** 还柜状态 */
export const ReturnBoxStatusEnum = {
  No: 0, // 未还柜
  Yes: 1, // 已还柜
} as const;
export type ReturnBoxStatusEnum = typeof ReturnBoxStatusEnum[keyof typeof ReturnBoxStatusEnum];

/** 业务类型（默认=未设置） */
export const WaybillTosEnum = {
  Unset: 0, // 未设置
  Door: 1, // 到门
  Port: 2, // 到港
  FakeDoor: 3, // 假门点
} as const;
export type WaybillTosEnum = typeof WaybillTosEnum[keyof typeof WaybillTosEnum];

/** 海运单类型（默认=未设置） */
export const WaybillTypeEnum = {
  Unset: 0, // 未设置
  Import: 1, // 进口
  Export: 2, // 出口
} as const;
export type WaybillTypeEnum = typeof WaybillTypeEnum[keyof typeof WaybillTypeEnum];

// ===== 账单枚举类型 =====

/**
 * 账单类型(跳号设计:1/10/20/21 常规,30/40/50 让账)
 *
 * 让账账单(BL-036):"占便宜方向"未归集账单的合法出口
 *   owner 是虚拟让账账户(is_writeoff_account=true)
 *   金额/Excel 模板/字段锁 完全复用对应正常账单
 */
export const BillTypeEnum = {
  ShipCompany: 1, // 船公司账单
  Partner: 10, // 合作公司账单
  Carrier: 20, // 承运方(合作车队)账单
  Self: 21, // 承运方(自己车)
  ConcedeCarrier: 30, // 承运方让账账单(我们少付)
  ConcedeShipCompany: 40, // 船公司让账账单(我们多收)
  ConcedePartner: 50, // 合作方让账账单(我们多收)
} as const;
export type BillTypeEnum = typeof BillTypeEnum[keyof typeof BillTypeEnum];

/** 判断账单类型是否为让账类型(BL-036) */
export const isConcedeBillType = (t: BillTypeEnum): boolean =>
  t === BillTypeEnum.ConcedeCarrier ||
  t === BillTypeEnum.ConcedeShipCompany ||
  t === BillTypeEnum.ConcedePartner;

/** 账单状态（跳号设计：0/1/10） */
export const BillStatusEnum = {
  Default: 0, // 默认
  Confirmed: 1, // 已确认
  Paid: 10, // 已付
} as const;
export type BillStatusEnum = typeof BillStatusEnum[keyof typeof BillStatusEnum];

/** 账单快照状态（两阶段 commit 4 态:0=已撤销 1=生效 2=生成中 3=生成失败） */
export const BillSnapshotStatusEnum = {
  Revoked: 0, // 已撤销（cancel_confirm 触发）
  Active: 1, // 生效（两阶段第二阶段写盘成功）
  Building: 2, // 生成中（两阶段第一阶段已 INSERT,写盘中）
  Failed: 3, // 生成失败（两阶段第二阶段写盘失败）
} as const;
export type BillSnapshotStatusEnum = typeof BillSnapshotStatusEnum[keyof typeof BillSnapshotStatusEnum];

// ===== 枚举文本映射（用于前端显示） =====

export const ShipReceiptStatusText: Record<ShipReceiptStatusEnum, string> = {
  [ShipReceiptStatusEnum.NoNeed]: '不需要上传',
  [ShipReceiptStatusEnum.NotUpload]: '未上传',
  [ShipReceiptStatusEnum.UploadedNotBack]: '已上传未拿回',
  [ShipReceiptStatusEnum.UploadedBack]: '已上传已拿回',
};

export const PartnerReceiptStatusText: Record<PartnerReceiptStatusEnum, string> = {
  [PartnerReceiptStatusEnum.NoNeed]: '不需要处理',
  [PartnerReceiptStatusEnum.NotTaken]: '未拿回',
  [PartnerReceiptStatusEnum.TakenNotPosted]: '已拿回未邮寄',
  [PartnerReceiptStatusEnum.NoNeedPost]: '不需要寄出',
  [PartnerReceiptStatusEnum.Posted]: '已寄出',
};

export const BoxConditionText: Record<BoxConditionEnum, string> = {
  [BoxConditionEnum.NoNeed]: '不需要报备',
  [BoxConditionEnum.NotExec]: '未报备',
  [BoxConditionEnum.Exec]: '已报备',
};

export const FhStatusText: Record<FhStatusEnum, string> = {
  [FhStatusEnum.Unreleased]: '未放货',
  [FhStatusEnum.Released]: '已放货',
};

export const RushStatusText: Record<RushStatusEnum, string> = {
  [RushStatusEnum.No]: '不加急',
  [RushStatusEnum.Yes]: '加急',
};

export const ReturnBoxStatusText: Record<ReturnBoxStatusEnum, string> = {
  [ReturnBoxStatusEnum.No]: '未还柜',
  [ReturnBoxStatusEnum.Yes]: '已还柜',
};

export const WaybillTosText: Record<WaybillTosEnum, string> = {
  [WaybillTosEnum.Unset]: '未设置',
  [WaybillTosEnum.Door]: '到门',
  [WaybillTosEnum.Port]: '到港',
  [WaybillTosEnum.FakeDoor]: '假门点',
};

export const WaybillTypeText: Record<WaybillTypeEnum, string> = {
  [WaybillTypeEnum.Unset]: '未设置',
  [WaybillTypeEnum.Import]: '进口',
  [WaybillTypeEnum.Export]: '出口',
};

export const BillTypeText: Record<BillTypeEnum, string> = {
  [BillTypeEnum.ShipCompany]: '船公司账单',
  [BillTypeEnum.Carrier]: '承运方(合作车队)账单',
  [BillTypeEnum.Partner]: '合作公司账单',
  [BillTypeEnum.Self]: '承运方(自己车)',
  [BillTypeEnum.ConcedeCarrier]: '承运方让账账单',
  [BillTypeEnum.ConcedeShipCompany]: '船公司让账账单',
  [BillTypeEnum.ConcedePartner]: '合作方让账账单',
};

/**
 * 账单角色短文案(UI 列表/Tag 用)
 * 同一公司可同时是 partner(应收)+ carrier(应付),Tag 标注便于在视觉上区分角色
 */
export const BillTypeRoleText: Record<BillTypeEnum, string> = {
  [BillTypeEnum.ShipCompany]: '船公司',
  [BillTypeEnum.Partner]: '合作方',
  [BillTypeEnum.Carrier]: '合作车队',
  [BillTypeEnum.Self]: '自己车',
  [BillTypeEnum.ConcedeCarrier]: '让账(承运方)',
  [BillTypeEnum.ConcedeShipCompany]: '让账(船公司)',
  [BillTypeEnum.ConcedePartner]: '让账(合作方)',
};

/** 账单角色 Tag 颜色(antd 色板,跨视图保持一致) */
export const BillTypeRoleColor: Record<BillTypeEnum, string> = {
  [BillTypeEnum.ShipCompany]: 'blue',
  [BillTypeEnum.Partner]: 'gold',
  [BillTypeEnum.Carrier]: 'purple',
  [BillTypeEnum.Self]: 'green',
  // 让账类型统一橙色,跟常规账单视觉区分(避免业务方误把让账当正常账单处理)
  [BillTypeEnum.ConcedeCarrier]: 'orange',
  [BillTypeEnum.ConcedeShipCompany]: 'orange',
  [BillTypeEnum.ConcedePartner]: 'orange',
};

/**
 * 账单标题尾缀(与后端 model.BillTypeName 保持字节级一致)
 * 用于前端从 bill.title 反解析 owner_name(title 格式: "{bill_month} {owner_name} {suffix}")
 * 后端改动时必须同步此处
 */
export const BillTypeTitleSuffix: Record<BillTypeEnum, string> = {
  [BillTypeEnum.ShipCompany]: '船公司账单',
  [BillTypeEnum.Partner]: '合作公司账单',
  [BillTypeEnum.Carrier]: '合作车队账单',
  [BillTypeEnum.Self]: '自己车账单',
  [BillTypeEnum.ConcedeCarrier]: '承运方让账账单',
  [BillTypeEnum.ConcedeShipCompany]: '船公司让账账单',
  [BillTypeEnum.ConcedePartner]: '合作方让账账单',
};

export const BillStatusText: Record<BillStatusEnum, string> = {
  [BillStatusEnum.Default]: '未确认',
  [BillStatusEnum.Confirmed]: '已确认未付',
  [BillStatusEnum.Paid]: '已付',
};

export const BillSnapshotStatusText: Record<BillSnapshotStatusEnum, string> = {
  [BillSnapshotStatusEnum.Revoked]: '已撤销',
  [BillSnapshotStatusEnum.Active]: '生效',
  [BillSnapshotStatusEnum.Building]: '生成中',
  [BillSnapshotStatusEnum.Failed]: '生成失败',
};

/** 实体角色短文案(实体净账查询用,与后端 service.EntityRole 字符串枚举对齐) */
export const EntityRoleText: Record<string, string> = {
  ship_company: '船公司(应收)',
  partner: '合作方(应收)',
  carrier_team: '合作车队(应付)',
  carrier_self: '自己车(应付)',
};

/** 实体角色 Tag 颜色 — 与 BillTypeRoleColor 同义,但 key 是字符串角色(后端 EntityRole) */
export const EntityRoleColor: Record<string, string> = {
  ship_company: 'blue',
  partner: 'gold',
  carrier_team: 'purple',
  carrier_self: 'green',
};
